// ==========================================
// api.js - PHP Backend API İstemcisi
// Her endpoint ayrı bir PHP dosyasına POST ile istek atar.
// ==========================================

export const SITE_BASE = "https://rezervioo.com";
export const API_BASE = `${SITE_BASE}/api`;
export const BUSINESS_DATAS_BASE = `${SITE_BASE}/business_datas`;

/**
 * İşletme logosu URL'si üretir.
 * cacheKey verilirse URL'e ?v=... ekleyerek cache kırar.
 * @param {number|string} businessId
 * @param {number|string|null} cacheKey
 * @returns {string}
 */
export function getBusinessLogoUrl(businessId, cacheKey = null) {
  if (!businessId) return "";
  const base = `${BUSINESS_DATAS_BASE}/${businessId}/logo/logo.png`;
  if (cacheKey === null || cacheKey === undefined || cacheKey === "") {
    return base;
  }
  return `${base}?v=${encodeURIComponent(String(cacheKey))}`;
}

/**
 * Genel fetch wrapper — her zaman POST ile body gönderir.
 * @param {string} endpoint - PHP dosya adı (örn: "getBusinesses.php")
 * @param {Object} body - PHP'ye gönderilecek veri objesi (opsiyonel)
 * @returns {Promise<any>} API'den dönen data
 */
async function apiFetch(endpoint, body = {}) {
  try {
    const response = await fetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP ${response.status} hatası`);
    }

    return data.data;
  } catch (error) {
    console.error(`API Hatası [${endpoint}]:`, error.message);
    throw error;
  }
}

/**
 * localStorage'dan auth_user_id'yi alır.
 * Admin-only endpointlerde otomatik olarak gönderilir.
 * @returns {number|null}
 */
function getAuthUserId() {
  try {
    const stored = localStorage.getItem("yerimhazir_user");
    if (stored) {
      const user = JSON.parse(stored);
      return user.id || null;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Kimlik doğrulama gerektiren API çağrısı.
 * auth_user_id otomatik eklenir.
 */
async function authenticatedFetch(endpoint, body = {}) {
  const authUserId = getAuthUserId();
  if (!authUserId) {
    throw new Error("Giriş yapmanız gerekiyor.");
  }
  return apiFetch(endpoint, { ...body, auth_user_id: authUserId });
}

/**
 * Admin API çağrısı — auth_user_id otomatik eklenir.
 */
async function adminFetch(endpoint, body = {}) {
  return authenticatedFetch(endpoint, body);
}

// ==========================================
// Auth API'leri
// ==========================================

/**
 * Kullanıcı girişi.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>} - { id, businessId, businessName, username, fullName, role }
 */
export async function login(username, password) {
  return apiFetch("login.php", { username, password });
}

// ==========================================
// İşletme (Business) API'leri
// ==========================================

/**
 * Tüm işletmeleri getirir.
 * @returns {Promise<Array>}
 */
export async function getBusinesses() {
  return apiFetch("getBusinesses.php");
}

/**
 * Tek bir işletmeyi ID ile getirir.
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function getBusinessById(id) {
  return apiFetch("getBusinessById.php", { id });
}

// ==========================================
// Hizmet (Service) API'leri
// ==========================================

/**
 * Bir işletmeye ait hizmetleri getirir.
 * @param {number|string} businessId
 * @returns {Promise<Array>}
 */
export async function getServicesByBusiness(businessId) {
  return apiFetch("getServicesByBusiness.php", { businessId });
}

// ==========================================
// Çalışan (Staff) API'leri
// ==========================================

/**
 * Bir işletmeye ait çalışanları getirir.
 * @param {number|string} businessId
 * @returns {Promise<Array>}
 */
export async function getStaffByBusiness(businessId) {
  return apiFetch("getStaffByBusiness.php", { businessId });
}

/**
 * Yeni çalışan ekler (Admin-only).
 * @param {Object} staffData - { businessId, username, password, fullName, role }
 * @returns {Promise<Object>}
 */
export async function addStaff(staffData) {
  return adminFetch("addStaff.php", staffData);
}

/**
 * Çalışan siler (Admin-only).
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function deleteStaff(id) {
  return adminFetch("deleteStaff.php", { id });
}

/**
 * Çalışan çalışma saatlerini günceller.
 * Çalışan kendi saatlerini, admin herkesin saatlerini güncelleyebilir.
 * @param {number|string} staffId
 * @param {Object} schedule - { start, end, closedDays, maxBookingDays }
 * @returns {Promise<Object>}
 */
export async function updateStaffHours(staffId, schedule) {
  return adminFetch("updateStaffHours.php", {
    staffId,
    start: schedule.start,
    end: schedule.end,
    closedDays: schedule.closedDays ?? "",
    maxBookingDays: schedule.maxBookingDays,
  });
}

// ==========================================
// Randevu (Appointment) API'leri
// ==========================================

/**
 * Bir işletmeye ait randevuları getirir.
 * @param {number|string} businessId
 * @param {string|null} date - Opsiyonel tarih filtresi (YYYY-MM-DD)
 * @param {number|string|null} staffId - Opsiyonel çalışan filtresi
 * @returns {Promise<Array>}
 */
export async function getAppointmentsByBusiness(businessId, date = null, staffId = null) {
  const body = { businessId };
  if (date) body.date = date;
  if (staffId) body.staffId = staffId;
  return apiFetch("getAppointmentsByBusiness.php", body);
}

/**
 * Yeni randevu oluşturur (çakışma kontrolü backend'de yapılır).
 * @param {Object} appointmentData
 * @returns {Promise<Object>}
 */
export async function createAppointment(appointmentData) {
  return apiFetch("createAppointment.php", appointmentData);
}

/**
 * Telefon numarası için geçici doğrulama kodu üretir ve saklar.
 * @param {string} phone
 * @returns {Promise<Object>}
 */
export async function requestPhoneVerificationCode(phone) {
  return apiFetch("requestPhoneVerificationCode.php", { phone });
}

/**
 * Telefon + kod doğrulaması yapar.
 * Eşleşme olursa geçici kod kaydı backend'de silinir.
 * @param {string} phone
 * @param {string} code
 * @returns {Promise<Object>}
 */
export async function verifyPhoneVerificationCode(phone, code) {
  return apiFetch("verifyPhoneVerificationCode.php", { phone, code });
}

/**
 * Randevu durumunu günceller.
 * Admin kendi işletmesindeki tüm randevuları, personel ise sadece kendi randevularını güncelleyebilir.
 * @param {number|string} id
 * @param {"pending"|"confirmed"|"cancelled"} status
 * @returns {Promise<Object>}
 */
export async function updateAppointmentStatus(id, status) {
  return authenticatedFetch("updateAppointmentStatus.php", { id, status });
}

// ==========================================
// Admin API'leri
// ==========================================

/**
 * Yeni hizmet ekler (Admin-only).
 * @param {Object} serviceData - { businessId, name, price, duration }
 * @returns {Promise<Object>}
 */
export async function addService(serviceData) {
  return adminFetch("addService.php", serviceData);
}

/**
 * Hizmeti günceller (Admin-only).
 * @param {number|string} id
 * @param {Object} serviceData - { name, price, duration }
 * @returns {Promise<Object>}
 */
export async function updateService(id, serviceData) {
  return adminFetch("updateService.php", { id, ...serviceData });
}

/**
 * Hizmeti siler (Admin-only).
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function deleteService(id) {
  return adminFetch("deleteService.php", { id });
}

/**
 * İşletme mesai saatlerini günceller (Admin-only).
 * @param {number|string} businessId
 * @param {Object} hours - { start: "09:00", end: "18:00" }
 * @returns {Promise<Object>}
 */
export async function updateBusinessHours(businessId, hours) {
  return adminFetch("updateBusinessHours.php", {
    businessId,
    start: hours.start,
    end: hours.end,
  });
}

/**
 * İşletme logosunu yükler (Admin-only, multipart/form-data).
 * @param {number|string} businessId
 * @param {File} logoFile - PNG dosyası
 * @returns {Promise<Object>}
 */
export async function uploadBusinessLogo(businessId, logoFile) {
  const authUserId = getAuthUserId();
  if (!authUserId) {
    throw new Error("Giriş yapmanız gerekiyor.");
  }

  const formData = new FormData();
  formData.append("auth_user_id", String(authUserId));
  formData.append("businessId", String(businessId));
  formData.append("logoFile", logoFile);

  const response = await fetch(`${API_BASE}/uploadBusinessLogo.php`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || `HTTP ${response.status} hatası`);
  }

  return data.data;
}
